import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { CountiesService, CountyResponse } from '@k12/school-services';
import { Subject, takeUntil } from 'rxjs';
import { startWith, map } from 'rxjs/operators';

/**
 * Reusable county selector component with filtering capability.
 * Implements ControlValueAccessor for seamless integration with Angular reactive forms.
 * 
 * @example
 * Basic usage:
 * <lib-county-selector
 *   formControlName="county"
 *   stateCode="nc"
 *   label="County"
 *   [required]="true"
 *   [readonly]="false">
 * </lib-county-selector>
 * 
 * @example
 * With data attributes for testing:
 * <lib-county-selector
 *   formControlName="county"
 *   stateCode="nc"
 *   dataPw="county-input"
 *   dataQa="county-select"
 *   dataTest="county-field"
 *   dataTestid="county-selector"
 *   dataE2e="county-e2e">
 * </lib-county-selector>
 * 
 * @example
 * With custom data attributes:
 * <lib-county-selector
 *   formControlName="county"
 *   stateCode="nc"
 *   [dataAttributes]="{ 'data-custom': 'value', 'data-tracking-id': '123' }">
 * </lib-county-selector>
 */
@Component({
  selector: 'lib-county-selector',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule
  ],
  templateUrl: './county-selector.component.html',
  styleUrl: './county-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountySelectorComponent),
      multi: true
    }
  ]
})
export class CountySelectorComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private readonly countiesService = inject(CountiesService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  /** State code for loading counties (e.g., 'nc', 'ca') */
  @Input() stateCode: string = '';

  /** Label text displayed above the selector */
  @Input() label: string = 'County';

  /** Placeholder text for the select input */
  @Input() placeholder: string = 'Select County';

  /** Whether the field is required (for styling only) */
  @Input() required: boolean = false;

  /** Whether the selector is in readonly mode */
  @Input() readonly: boolean = false;

  /** Data attributes for testing */
  @Input() dataPw: string = '';
  @Input() dataQa: string = '';
  @Input() dataTest: string = '';
  @Input() dataTestid: string = '';
  @Input() dataE2e: string = '';
  
  /** Additional custom data attributes as key-value pairs */
  @Input() dataAttributes: Record<string, string> = {};

  protected counties = signal<CountyResponse[]>([]);
  protected filteredCounties = signal<CountyResponse[]>([]);
  protected filterControl = new FormControl('');
  protected isLoading = signal<boolean>(false);
  protected isDisabled = signal<boolean>(false);

  private selectedValue: string | null = null;
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    // Load counties when state code is provided
    if (this.stateCode) {
      this.loadCounties(this.stateCode);
    }

    // Set up filter logic
    this.filterControl.valueChanges
      .pipe(
        startWith(''),
        map((filterValue) => this.filterCounties(filterValue || '')),
        takeUntil(this.destroy$)
      )
      .subscribe((filtered) => {
        this.filteredCounties.set(filtered);
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handles selection change from mat-select
   */
  protected onSelectionChange(value: string | null): void {
    this.selectedValue = value;
    this.onChange(value);
    this.onTouched();
  }

  /**
   * Handles blur event to mark control as touched
   */
  protected onBlur(): void {
    this.onTouched();
  }

  /**
   * Gets all data attributes as an object for binding
   */
  protected getDataAttributes(): Record<string, string> {
    const attrs: Record<string, string> = {};
    
    if (this.dataPw) attrs['data-pw'] = this.dataPw;
    if (this.dataQa) attrs['data-qa'] = this.dataQa;
    if (this.dataTest) attrs['data-test'] = this.dataTest;
    if (this.dataTestid) attrs['data-testid'] = this.dataTestid;
    if (this.dataE2e) attrs['data-e2e'] = this.dataE2e;
    
    // Merge with any additional custom data attributes
    return { ...attrs, ...this.dataAttributes };
  }

  /**
   * Clears the filter input
   */
  protected clearFilter(): void {
    this.filterControl.setValue('', { emitEvent: true });
  }

  // ControlValueAccessor implementation

  writeValue(value: string | null): void {
    this.selectedValue = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
    if (isDisabled) {
      this.filterControl.disable({ emitEvent: false });
    } else {
      this.filterControl.enable({ emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  // Private methods

  private loadCounties(stateCode: string): void {
    this.isLoading.set(true);
    this.countiesService.getCountiesByState(stateCode.toLowerCase())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (counties) => {
          this.counties.set(counties);
          this.filteredCounties.set(counties);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.counties.set([]);
          this.filteredCounties.set([]);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        }
      });
  }

  private filterCounties(filterValue: string): CountyResponse[] {
    const normalizedFilter = filterValue.toLowerCase().trim();
    if (!normalizedFilter) {
      return this.counties();
    }
    return this.counties().filter((county) =>
      county.name.toLowerCase().includes(normalizedFilter)
    );
  }

  protected get value(): string | null {
    return this.selectedValue;
  }
}
